import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import {groupBy} from 'lodash';
import {getPercent} from '@ey/utils';
import {
	readinessReportCategories,
	reportingBuckets,
	dashboardTabId,
	reportingFilterId,
	dashboardChartColors
} from '../../../../../util/uiconstants';
import {labels, currentResource} from '../../../../../util/utils';
import normalizeDashboardFilters from '../../../../../util/dashboard/normalizeDashboardFilters';
import Tile from '../../../../Common/Tile';
import GroupedStackedBarChart from '../../../../Common/Charts/GroupedStackedBarChart';
import {
	updateFilterDashboardTiles,
	clearFilterDashboardTiles
} from '../../../../../actions/Metrics/dashboardFiltersAction';

const ReportReadiness = (props) => {
	const {engagementMetrics} = props;
	const numberFormat = useSelector(({userPreferences}) => userPreferences.numberFormat);
	const dispatch = useDispatch();
	const [series, setSeries] = useState([]);
	const today = moment();

	useEffect(() => {
		buildSeries(engagementMetrics);
	}, [engagementMetrics]);

	const buildSeries = (engagementMetrics) => {
		let commingDueData = getFormatedSeries(engagementMetrics, false);
		let overDueData = getFormatedSeries(engagementMetrics, true);
		setSeries([...commingDueData, ...overDueData]);
	};

	const getFormatedSeries = (engagementMetrics, isOverDue) => {
		return reportingBuckets.map((item) => {
			//filter all metrics to get the valid ones for the current bucket percentage
			const validMetrics = engagementMetrics
				.filter(
					(engagementMetric) =>
						(isOverDue ? isOverdue(engagementMetric.relDate) : !isOverdue(engagementMetric.relDate)) &&
						isInRange(item, !engagementMetric.allTC ? 0 : getPercent(engagementMetric.allTC, engagementMetric.allTTot))
				)
				//add a category for each valid engagement
				.map((engagementMetric) => {
					return {...engagementMetric, category: getCategory(engagementMetric.relDate)};
				});
			//group by category to count the number of engagements for each category
			const groupedMetrics = groupBy(validMetrics, 'category');

			if (isOverDue) {
				return {
					name: item.name,
					color: item.overDueColor,
					data: [
						[
							readinessReportCategories.overDueUnder1Week,
							groupedMetrics[readinessReportCategories.overDueUnder1Week]?.length || 0
						],
						[
							readinessReportCategories.overDue12Weeks,
							groupedMetrics[readinessReportCategories.overDue12Weeks]?.length || 0
						],
						[
							readinessReportCategories.overDueOver2Weeks,
							groupedMetrics[readinessReportCategories.overDueOver2Weeks]?.length || 0
						]
					]
				};
			} else {
				return {
					name: item.name,
					color: item.commingDueColor,
					data: [
						[
							readinessReportCategories.comingDueOver2Weeks,
							groupedMetrics[readinessReportCategories.comingDueOver2Weeks]?.length || 0
						],
						[
							readinessReportCategories.comingDue12Weeks,
							groupedMetrics[readinessReportCategories.comingDue12Weeks]?.length || 0
						],
						[
							readinessReportCategories.comingDueUnder1Week,
							groupedMetrics[readinessReportCategories.comingDueUnder1Week]?.length || 0
						]
					]
				};
			}
		});
	};

	const getCategory = (expectedReportReleaseDate) => {
		const reportDate = moment(expectedReportReleaseDate);
		const diff = Math.abs(today.diff(reportDate, 'week'));

		switch (diff) {
			case 0: {
				if (isOverdue(reportDate)) {
					return readinessReportCategories.overDueUnder1Week;
				} else {
					return readinessReportCategories.comingDueUnder1Week;
				}
			}
			case 1: {
				if (isOverdue(reportDate)) {
					return readinessReportCategories.overDue12Weeks;
				} else {
					return readinessReportCategories.comingDue12Weeks;
				}
			}
			default: {
				if (isOverdue(reportDate)) {
					return readinessReportCategories.overDueOver2Weeks;
				} else {
					return readinessReportCategories.comingDueOver2Weeks;
				}
			}
		}
	};

	const isInRange = (bucketItem, percentage) => {
		return bucketItem.min <= percentage && bucketItem.max >= percentage;
	};

	const isOverdue = (reportDate) => {
		return today.isAfter(reportDate);
	};

	const getFilteredEngagementIds = (reportingBucket, engagementIsOverdue) => {
		return engagementMetrics
			.filter(
				(engagementMetric) =>
					(engagementIsOverdue ? isOverdue(engagementMetric.relDate) : !isOverdue(engagementMetric.relDate)) &&
					isInRange(
						reportingBucket,
						!engagementMetric.allTC ? 0 : getPercent(engagementMetric.allTC, engagementMetric.allTTot)
					)
			)
			.map((eng) => eng.engId);
	};

	const getCategoryReportingBucket = (color) => {
		switch (color) {
			case dashboardChartColors.teal[0]: //coming due 0% || over due 0%
				return reportingBuckets.filter((rb) => {
					return rb.name === '0%';
				})[0];
			case dashboardChartColors.teal[1]: // coming due
			case dashboardChartColors.red[1]: // over due
				return reportingBuckets.filter((rb) => {
					return rb.name === '1-49%';
				})[0];
			case dashboardChartColors.teal[2]: // coming due
			case dashboardChartColors.red[2]: // over due
				return reportingBuckets.filter((rb) => {
					return rb.name === '50-79%';
				})[0];
			case dashboardChartColors.teal[3]: // coming due
			case dashboardChartColors.red[3]: // over due
				return reportingBuckets.filter((rb) => {
					return rb.name === '>80%';
				})[0];
			default:
				return null;
		}
	};

	const handlerOnClearFilter = () => {
		dispatch(clearFilterDashboardTiles(dashboardTabId.reporting));
	};

	const handlerOnGraphicClick = (highChartEvent) => {
		const categoryXPoint = highChartEvent.point?.options?.x;
		const categoryRangeColor = highChartEvent.point?.color;
		const categoryReportingBucket = getCategoryReportingBucket(categoryRangeColor);
		let serieLabel = '';
		let filteredEngagementIds = [];

		switch (categoryXPoint) {
			case readinessReportCategories.comingDueOver2Weeks:
			case readinessReportCategories.comingDue12Weeks:
			case readinessReportCategories.comingDueUnder1Week:
				serieLabel = labels.comingDue;
				filteredEngagementIds = getFilteredEngagementIds(categoryReportingBucket, false);
				break;
			case readinessReportCategories.overDueUnder1Week:
			case readinessReportCategories.overDue12Weeks:
			case readinessReportCategories.overDueOver2Weeks:
				serieLabel = labels.overdueLabel;
				filteredEngagementIds = getFilteredEngagementIds(categoryReportingBucket, true);
				break;
		}

		const dashboardFilters = normalizeDashboardFilters(
			[
				{
					serieLabel: categoryReportingBucket.name,
					categoryLabel: `${serieLabel.charAt(0).toUpperCase() + serieLabel.slice(1)}`,
					type: reportingFilterId.completionByEngagement,
					onClearChip: handlerOnClearFilter
				}
			],
			filteredEngagementIds,
			handlerOnClearFilter
		);

		dispatch(updateFilterDashboardTiles(dashboardTabId.reporting, dashboardFilters));
	};

	const tileProps = {
		title: labels.taskCompletionByEngagement,
		subTitle: labels.basedOnReportDateProximityForEachEngagement,
		isEmpty: engagementMetrics.length === 0,
		emptyTitle: labels.thereIsNoDataToDisplay,
		emptySubtitle: labels.selectadditionalEntitiesToContinue
	};

	return (
		<Tile {...tileProps}>
			<GroupedStackedBarChart
				categories={currentResource.reportReadinessSeries.map((item) => item.label)}
				series={series}
				title={labels.engagementCount}
				numberFormat={numberFormat}
				noDataTitle={labels.thereIsNoDataToDisplay}
				noDataSubtitle={labels.selectadditionalEntitiesToContinue}
				totalEngagements={engagementMetrics.length}
				onGraphicClick={handlerOnGraphicClick}
			/>
		</Tile>
	);
};

ReportReadiness.defaultProps = {
	engagementMetrics: []
};

ReportReadiness.propTypes = {
	engagementMetrics: PropTypes.arrayOf(
		PropTypes.shape({
			cRAccepted: PropTypes.number,
			cRDue3: PropTypes.number,
			cRDue5: PropTypes.number,
			cRDue10: PropTypes.number,
			cRDue30: PropTypes.number,
			cRDueTom: PropTypes.number,
			cRNotSent: PropTypes.number,
			cROD: PropTypes.number,
			cRPendResp: PropTypes.number,
			cRReceived: PropTypes.number,
			cRRetrn: PropTypes.number,
			cRTotal: PropTypes.number,
			cTComplSend: PropTypes.number,
			cTNotSent: PropTypes.number,
			cTOD: PropTypes.number,
			cTPartialSend: PropTypes.number,
			cTRetrn: PropTypes.number,
			cTTotal: PropTypes.number,
			cTUnlinkTot: PropTypes.number,
			conTaskClo: PropTypes.number,
			conTaskInProg: PropTypes.number,
			conTaskInRev: PropTypes.number,
			conTaskOpen: PropTypes.number,
			conTaskTotal: PropTypes.number,
			conTested: PropTypes.number,
			conTotal: PropTypes.number,
			defDesign: PropTypes.number,
			defOper: PropTypes.number,
			defOperAndDes: PropTypes.number,
			engDes: PropTypes.string,
			engId: PropTypes.number,
			engKey: PropTypes.number,
			gAType: PropTypes.number,
			hasAPEQRSO: PropTypes.bool,
			hasAPPICSO: PropTypes.bool,
			hasCRAEQRSO: PropTypes.bool,
			hasCRAPICSO: PropTypes.bool,
			isEQRCRASODlyd: PropTypes.bool,
			isEQREng: PropTypes.bool,
			isEQRInv: PropTypes.bool,
			isPartAPSODlyd: PropTypes.bool,
			isPartCRASODlyd: PropTypes.bool,
			isPartInvDlyd: PropTypes.bool,
			relDate: PropTypes.string,
			allTC: PropTypes.number,
			allTTot: PropTypes.number
		})
	)
};

export default ReportReadiness;
